import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Button, CircularProgress} from 'views/components';
import {Translate} from 'views/module';

import MessageFeedBox from './messages/MessageFeedBox';
import LogFeedBox from './logs/LogFeedBox';

{/* List of Client messages and Logs associated with the Client */}
const CorrespondanceFeed = (props) => {
  // Comments to display on client

  const {
    clientRn,

    // props for MessageFeedBox
    clientMessages: messages,
    clientLogs,

    clientMessagesLoading,
    clientMessagesLastEvaluatedKey,

    clientLogsLastEvaluatedKey,
    clientLogsLoading,

    sortDir,
    corType,
    clientBackend,
    clientData,
    onReplyToMessage,
  } = props;
  const targetMessageRn = props.focusedClientMessageRn || new URLSearchParams(window.location.search).get('messageRn');
  const targetMessageRef = React.useRef(null);
  const hasFocusedTargetMessageRef = React.useRef(false);
  const [highlightTargetMessage, setHighlightTargetMessage] = React.useState(Boolean(targetMessageRn));

  React.useEffect(() => {
    hasFocusedTargetMessageRef.current = false;
    setHighlightTargetMessage(Boolean(targetMessageRn));
  }, [targetMessageRn]);

  const loadMoreMessages = () => {
    if (clientMessagesLastEvaluatedKey && !clientMessagesLoading) {
      clientBackend.load.loadMessages(clientRn, clientMessagesLastEvaluatedKey);
    }
    if (clientLogsLastEvaluatedKey && !clientLogsLoading) {
      clientBackend.load.loadLogs(clientRn, clientLogsLastEvaluatedKey);
    }
  };

  const messagesToDisplayCaluclator = React.useCallback(() => {
    const messagesFilter = Object.values(messages)
        .filter((message) => {
          return corType[message.messageType];
        });

    let oldestMessage = null;
    if (clientMessagesLastEvaluatedKey) {
      oldestMessage = Object.values(messages)
          .reduce((oldest, item) => {
            return new Date(item.createdAt) < new Date(oldest.createdAt) ? item : oldest;
          });
    }

    const logsFilter = Object.values(clientLogs)
        .filter((log) => {
          if (corType['LOGS']) {
            // limit logs shows to the oldest message
            if (oldestMessage && new Date(oldestMessage.createdAt) > new Date(log.createdAt) ) {
              return false;
            }

            return true;
          }
        });

    const messagesAndlogs = [
      ...(messagesFilter.map((e) => {
        return {...e, type: 'MESSAGE'};
      })),
      ...(logsFilter.map((e) => {
        return {...e, type: 'LOGS'};
      })),
    ];

    const sortedMessages = messagesAndlogs.sort((a, b) => {
      const dateA = new Date(a.createdAt);
      const dateB = new Date(b.createdAt);

      if (sortDir === 'DESC') {
        return dateB - dateA;
      } else {
        return dateA - dateB;
      }
    });

    return sortedMessages;
  }, [messages, clientLogs, sortDir, corType]);

  const messagesToDisplay = messagesToDisplayCaluclator();

  React.useEffect(() => {
    if (!targetMessageRn || messages[targetMessageRn] || !clientMessagesLastEvaluatedKey || clientMessagesLoading) {
      return;
    }

    clientBackend.load.loadMessages(clientRn, clientMessagesLastEvaluatedKey);
  }, [clientBackend, clientMessagesLastEvaluatedKey, clientMessagesLoading, clientRn, messages, targetMessageRn]);

  React.useEffect(() => {
    if (!targetMessageRn || !messages[targetMessageRn] || hasFocusedTargetMessageRef.current) {
      return undefined;
    }

    const animationFrame = window.requestAnimationFrame(() => {
      hasFocusedTargetMessageRef.current = true;
      targetMessageRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
    });
    const highlightTimer = window.setTimeout(() => setHighlightTargetMessage(false), 4000);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(highlightTimer);
    };
  }, [messages, targetMessageRn]);

  const moreMessages = clientMessagesLoading || clientLogsLoading;
  const showLoadMoreButton =
    (clientMessagesLastEvaluatedKey && !clientMessagesLoading) ||
    (clientLogsLastEvaluatedKey && !clientLogsLoading);
  const showLoadMoreSection = moreMessages || showLoadMoreButton;
  const loadMoreSection = (
    <Stack
      spacing={2}
      sx={{
        alignItems: 'center',
      }}
    >
      {
        moreMessages &&
        <CircularProgress/>
      }
      {
        showLoadMoreButton &&
        <Button
          color={'primary'}
          onClick={loadMoreMessages}
        >
          <Translate expression={'client.ClientDetail.Correspondance.Messages.LoadMore'} />
        </Button>
      }
    </Stack>
  );

  return (
    <Stack
      spacing={2.5}
      sx={{
        px: {md: 2.5, xs: 1.5},
        pt: 2.5,
        pb: 2,
      }}
    >
      {
        sortDir === 'ASC' && showLoadMoreSection && loadMoreSection
      }
      <Stack spacing={2.5}>
        {
          messagesToDisplay.map((message) => {
            if (message.type === 'MESSAGE') {
              return (
                <Box
                  key={message.messageRn}
                  ref={message.messageRn === targetMessageRn ? targetMessageRef : undefined}
                  sx={{
                    borderRadius: 2,
                    outline: message.messageRn === targetMessageRn && highlightTargetMessage ? '3px solid' : '0 solid',
                    outlineColor: 'warning.main',
                    transition: 'outline-width 300ms ease',
                  }}
                >
                  <MessageFeedBox
                    message={message}
                    clientBackend={clientBackend}
                    relationships={clientData.relationships}
                    onReplyToMessage={onReplyToMessage}
                  />
                </Box>
              );
            } else if (message.type === 'LOGS') {
              return (
                <LogFeedBox
                  key={message.clientLogRn}
                  log={message}
                  clientBackend={clientBackend}
                />
              );
            }
            return null;
          })
        }
      </Stack>
      {
        sortDir === 'DESC' && showLoadMoreSection && loadMoreSection
      }
    </Stack>
  );
};
CorrespondanceFeed.propTypes = {
  clientRn: PropTypes.string.isRequired,

  // props for MessageFeedBox
  clientMessages: PropTypes.object.isRequired,
  clientLogs: PropTypes.object.isRequired,

  clientMessagesLoading: PropTypes.bool.isRequired,
  clientMessagesLastEvaluatedKey: PropTypes.string,

  clientLogsLoading: PropTypes.bool.isRequired,
  clientLogsLastEvaluatedKey: PropTypes.string,

  sortDir: PropTypes.string.isRequired,
  corType: PropTypes.shape({
    ANNOUNCEMENTS: PropTypes.bool.isRequired,
    COMMENTS: PropTypes.bool.isRequired,
    LOGS: PropTypes.bool.isRequired,
    CALENDLY_UPDATE: PropTypes.bool.isRequired,
    DIALPAD_SMS_TEXT: PropTypes.bool.isRequired,
    DIALPAD_CALL_TRANSCRIPT: PropTypes.bool.isRequired,
    EMAIL: PropTypes.bool.isRequired,
  }).isRequired,

  clientBackend: PropTypes.object.isRequired,
  clientData: PropTypes.shape({
    relationships: PropTypes.object,
  }).isRequired,
  onReplyToMessage: PropTypes.func.isRequired,
  focusedClientMessageRn: PropTypes.string,

};

export default React.memo(CorrespondanceFeed);
